<template>
  <div id="controls">
    <span
      class="nomlog-summary"
      v-show="session.voteHistory.length && session.sessionId"
      @click="toggleModal('voteHistory')"
      :title="`${session.voteHistory.length} ${$t(
        'session.' +
          (session.voteHistory.length == 1
            ? 'recentNomination'
            : 'recentNominations'),
      )}`"
    >
      <font-awesome-icon icon="book-dead" />
      {{ session.voteHistory.length }}
    </span>
    <span
      class="session"
      :class="{
        spectator: session.isSpectator,
        reconnecting: session.isReconnecting,
      }"
      v-if="session.sessionId"
      @click="leaveSession"
      :title="`${session.playerCount} ${$t('session.otherPlayersInSession')}${
        session.ping ? ' (' + session.ping + $t('session.msLatency') + ')' : ''
      }`"
    >
      <font-awesome-icon icon="broadcast-tower" />
      {{ session.playerCount }}
    </span>
    <div class="menu" :class="{ open: grimoire.isMenuOpen }">
      <font-awesome-icon icon="cog" @click="toggleMenu" />
      <ul>
        <li class="tabs" :class="tab">
          <font-awesome-icon icon="book-open" @click="tab = 'grimoire'" />
          <font-awesome-icon icon="broadcast-tower" @click="tab = 'session'" />
          <font-awesome-icon
            icon="users"
            v-if="!session.isSpectator"
            @click="tab = 'players'"
          />
          <font-awesome-icon icon="theater-masks" @click="tab = 'characters'" />
          <font-awesome-icon icon="question" @click="tab = 'help'" />
        </li>

        <template v-if="tab === 'grimoire'">
          <!-- Grimoire -->
          <li class="headline">{{ $t("menu.grimoire") }}</li>
          <li @click="toggleGrimoire" v-if="players.length">
            <template v-if="!grimoire.isPublic">{{
              $t("settings.publicTownSquare")
            }}</template>
            <template v-if="grimoire.isPublic">{{
              $t("menu.grimoire")
            }}</template>
            <em>[G]</em>
          </li>
          <li @click="toggleNight" v-if="!session.isSpectator">
            <template v-if="!grimoire.isNight">{{
              $t("settings.nightMode")
            }}</template>
            <template v-if="grimoire.isNight">{{
              $t("common.day") || "Day"
            }}</template>
            <em>[S]</em>
          </li>
          <li @click="toggleNightOrder" v-if="players.length">
            {{ $t("menu.nightOrder") }}
            <em>
              <font-awesome-icon
                :icon="[
                  'fas',
                  grimoire.isNightOrder ? 'check-square' : 'square',
                ]"
              />
            </em>
          </li>
          <li v-if="players.length">
            {{ $t("settings.zoom") }}
            <em>
              <font-awesome-icon
                @click="setZoom(grimoire.zoom - 1)"
                icon="search-minus"
              />
              {{ Math.round(100 + grimoire.zoom * 10) }}%
              <font-awesome-icon
                @click="setZoom(grimoire.zoom + 1)"
                icon="search-plus"
              />
            </em>
          </li>
          <li @click="setBackground">
            {{ $t("settings.background") }}
            <em><font-awesome-icon icon="image" /></em>
          </li>
          <li v-if="!edition.isOfficial" @click="imageOptIn">
            <small>{{ $t("settings.imageOptIn") }}</small>
            <em
              ><font-awesome-icon
                :icon="[
                  'fas',
                  grimoire.isImageOptIn ? 'check-square' : 'square',
                ]"
            /></em>
          </li>
          <li @click="toggleStatic">
            {{ $t("settings.staticBackground") }}
            <em
              ><font-awesome-icon
                :icon="['fas', grimoire.isStatic ? 'check-square' : 'square']"
            /></em>
          </li>
          <li @click="toggleMuted">
            {{ $t("settings.muted") }}
            <em
              ><font-awesome-icon
                :icon="['fas', grimoire.isMuted ? 'volume-mute' : 'volume-up']"
            /></em>
          </li>
          <li @click="toggleLanguage">
            Language / 语言
            <em>{{ $i18n.locale === "zh-CN" ? "中文" : "EN" }}</em>
          </li>
          <li @click="setNickname">
            {{ $t("settings.nickname") }}
            <em>{{ grimoire.nickname || $t("settings.notSet") }}</em>
          </li>
          <li @click="clearStorage">
            {{ $t("settings.clearStorage") }}
            <em><font-awesome-icon icon="broom" /></em>
          </li>
        </template>

        <template v-if="tab === 'session'">
          <!-- Session -->
          <li class="headline" v-if="session.sessionId">
            {{
              session.isSpectator ? $t("session.spectator") : $t("session.host")
            }}
          </li>
          <li class="headline" v-else>{{ $t("session.liveSession") }}</li>
          <template v-if="!session.sessionId">
            <li @click="hostSession">
              {{ $t("session.host") }} (Storyteller)<em>[H]</em>
            </li>
            <li @click="joinSession">
              {{ $t("session.join") }} (Player)<em>[J]</em>
            </li>
          </template>
          <template v-else>
            <li v-if="session.ping">
              Delay to {{ session.isSpectator ? "host" : "players" }}
              <em>{{ session.ping }}ms</em>
            </li>
            <li @click="copySessionUrl">
              {{ $t("gameState.copyToClipboard") }}
              <em><font-awesome-icon icon="copy" /></em>
            </li>
            <li v-if="!session.isSpectator" @click="distributeRoles">
              {{ $t("session.distributeRoles") }}
              <em><font-awesome-icon icon="theater-masks" /></em>
            </li>
            <li v-if="!session.isSpectator && players.length" @click="sendBluffs">
              {{ $t("session.sendBluffs") }}
              <em><font-awesome-icon icon="user-secret" /></em>
            </li>
            <li
              v-if="session.voteHistory.length || !session.isSpectator"
              @click="toggleModal('voteHistory')"
            >
              {{ $t("voting.voteHistory") }}<em>[V]</em>
            </li>
            <li @click="leaveSession">
              {{
                session.isSpectator
                  ? $t("session.leave")
                  : $t("session.dissolveRoom")
              }}
              <em>{{ session.sessionId }}</em>
            </li>
          </template>
        </template>

        <template v-if="tab === 'players' && !session.isSpectator">
          <!-- Users -->
          <li class="headline">{{ $t("player.players") }}</li>
          <li @click="addPlayer" v-if="players.length < 20">
            {{ $t("player.addPlayer") }}<em>[A]</em>
          </li>
          <li @click="randomizeSeatings" v-if="players.length > 2">
            {{ $t("player.randomizeSeating") }}
            <em><font-awesome-icon icon="dice" /></em>
          </li>
          <li @click="clearPlayers" v-if="players.length">
            {{ $t("player.clearPlayers") }}
            <em><font-awesome-icon icon="trash-alt" /></em>
          </li>
        </template>

        <template v-if="tab === 'characters'">
          <!-- Characters -->
          <li class="headline">{{ $t("menu.roles") }}</li>
          <li v-if="!session.isSpectator" @click="toggleModal('edition')">
            {{ $t("edition.selectEdition") }}
            <em>[E]</em>
          </li>
          <li
            @click="toggleModal('roles')"
            v-if="!session.isSpectator && players.length > 4"
          >
            {{ $t("menu.roles") }}
            <em>[C]</em>
          </li>
          <li v-if="!session.isSpectator" @click="toggleModal('fabled')">
            {{ $t("menu.fabled") }}
            <em><font-awesome-icon icon="dragon" /></em>
          </li>
          <li @click="clearRoles" v-if="players.length">
            {{ $t("common.clear") }}
            <em><font-awesome-icon icon="trash-alt" /></em>
          </li>
        </template>

        <template v-if="tab === 'help'">
          <!-- Help -->
          <li class="headline">{{ $t("common.help") || "Help" }}</li>
          <li @click="toggleModal('reference')">
            {{ $t("menu.characterReference") }}
            <em>[R]</em>
          </li>
          <li @click="toggleModal('nightOrder')">
            {{ $t("menu.nightOrder") }}
            <em>[N]</em>
          </li>
          <li @click="toggleModal('gameState')">
            {{ $t("menu.gameState") }}
            <em><font-awesome-icon icon="file-code" /></em>
          </li>
          <li>
            <a href="https://discord.gg/Gd7ybwWbFk" target="_blank">
              {{ $t("session.joinDiscord") }}
            </a>
            <em>
              <a href="https://discord.gg/Gd7ybwWbFk" target="_blank">
                <font-awesome-icon :icon="['fab', 'discord']" />
              </a>
            </em>
          </li>
          <li>
            <a href="https://github.com/bra1n/townsquare" target="_blank">
              {{ $t("session.sourceCode") }}
            </a>
            <em>
              <a href="https://github.com/bra1n/townsquare" target="_blank">
                <font-awesome-icon :icon="['fab', 'github']" />
              </a>
            </em>
          </li>
        </template>
      </ul>
    </div>
    
    <!-- Distribute Roles Confirmation Popup -->
    <DistributeRolesModal
      :show="showDistributeRolesModal"
      :initialCheckboxValue="grimoire.autoSendBluffsToDemon"
      @confirm="handleDistributeRolesConfirm"
      @cancel="showDistributeRolesModal = false"
    />
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import DistributeRolesModal from "./modals/DistributeRolesModal";

export default {
  components: {
    DistributeRolesModal,
  },
  computed: {
    ...mapState(["grimoire", "session", "edition"]),
    ...mapState("players", ["players"]),
  },
  data() {
    return {
      tab: "grimoire",
      showDistributeRolesModal: false,
    };
  },
  methods: {
    setBackground() {
      const background = prompt(this.$t("settings.enterBackgroundUrl"));
      if (background || background === "") {
        this.$store.commit("setBackground", background);
      }
    },
    hostSession() {
      if (this.session.sessionId) return;
      
      // Check if user has a nickname, if not, prompt to set one first
      if (!this.grimoire.nickname || !this.grimoire.nickname.trim()) {
        console.log("[Menu] No nickname set, opening nickname modal first");
        this.$store.commit("toggleModal", "nickname");
        // Store pending action to execute after nickname is set
        this.$store.commit("session/setPendingAction", "hostSession");
        return;
      }
      
      // Emit event to parent to open the session setup modal
      this.$emit("hostSession");
    },
    copySessionUrl() {
      const url = window.location.href.split("#")[0];
      const link = url + "#" + this.session.sessionId;
      this.copyToClipboard(link);
    },
    copyToClipboard(text) {
      // Try modern Clipboard API first (HTTPS only)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(err => {
          console.log("Modern clipboard failed, using fallback:", err);
          this.fallbackCopyToClipboard(text);
        });
      } else {
        // Use fallback for HTTP or unsupported browsers
        this.fallbackCopyToClipboard(text);
      }
    },
    fallbackCopyToClipboard(text) {
      // Create a temporary textarea element
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      
      try {
        textarea.select();
        document.execCommand("copy");
        console.log("Copied to clipboard using fallback method");
      } catch (err) {
        console.log("Fallback clipboard also failed:", err);
      } finally {
        document.body.removeChild(textarea);
      }
    },
    distributeRoles() {
      if (this.session.isSpectator) return;
      this.showDistributeRolesModal = true;
    },
    handleDistributeRolesConfirm(sendBluffsToLunatic) {
      this.showDistributeRolesModal = false;
      
      // Save checkbox state
      this.$store.commit("setAutoSendBluffsToDemon", sendBluffsToLunatic);
      
      this.$store.commit("session/distributeRoles", true);
      setTimeout(
        (() => {
          this.$store.commit("session/distributeRoles", false);
        }).bind(this),
        2000,
      );
      
      // If checkbox is checked, also send bluffs to demon
      if (sendBluffsToLunatic) {
        setTimeout(() => {
          this.sendBluffsToDemon();
        }, 2500);
      }
    },
    sendBluffs() {
      if (this.session.isSpectator) return;
      // Show selection modal for who to send bluffs to
      this.$emit("sendBluffs");
    },
    sendBluffsToDemon() {
      // Find demon player
      const demon = this.players.find(p => p.role && p.role.team === 'demon');
      if (demon && demon.id) {
        this.$store.commit("session/sendBluffsToPlayer", demon.id);
      }
    },
    imageOptIn() {
      this.toggleImageOptIn();
    },
    joinSession() {
      if (this.session.sessionId) return this.leaveSession();
      
      // Check if user has a nickname, if not, prompt to set one first
      if (!this.grimoire.nickname || !this.grimoire.nickname.trim()) {
        console.log("[Menu] No nickname set, opening nickname modal first");
        this.$store.commit("toggleModal", "nickname");
        // Store pending action to execute after nickname is set
        this.$store.commit("session/setPendingAction", "joinSession");
        return;
      }
      
      this.$store.commit("toggleModal", "joinSession");
    },
    leaveSession() {
      const isHost = !this.session.isSpectator;

      this.$store.commit("setConfirmModal", {
        title: this.$t(isHost ? "session.dissolveRoom" : "session.leave"),
        message: this.$t(
          isHost
            ? "session.confirmDissolveRoom"
            : "session.confirmLeaveSession",
        ),
        confirmText: this.$t("common.confirm"),
        cancelText: this.$t("common.cancel"),
        onConfirm: () => {
          if (isHost) {
            // Host: Dissolve room and clear all game data
            this.$store.commit("players/clear");
            this.$store.commit("session/clearVoteHistory");
            // Clear roles if needed
            if (this.session.nomination) {
              this.$store.commit("session/nomination");
            }
          }
          // Both: Leave session
          this.$store.commit("session/setSpectator", false);
          this.$store.commit("session/setSessionId", "");
        },
      });
    },
    addPlayer() {
      if (this.session.isSpectator) return;
      if (this.players.length >= 20) return;
      // Directly add an empty seat without prompting for name
      this.$store.commit("players/add", "__EMPTY_SEAT__");
    },
    randomizeSeatings() {
      if (this.session.isSpectator) return;
      this.$store.commit("setConfirmModal", {
        title: this.$t("settings.randomizeSeating"),
        message: this.$t("settings.confirmRandomizeSeating"),
        confirmText: this.$t("common.confirm"),
        cancelText: this.$t("common.cancel"),
        onConfirm: () => {
          this.$store.dispatch("players/randomize");
        },
      });
    },
    clearPlayers() {
      if (this.session.isSpectator) return;
      this.$store.commit("setConfirmModal", {
        title: this.$t("player.clearPlayers"),
        message: this.$t("messages.confirmClearPlayers"),
        confirmText: this.$t("common.confirm"),
        cancelText: this.$t("common.cancel"),
        onConfirm: () => {
          // abort vote if in progress
          if (this.session.nomination) {
            this.$store.commit("session/nomination");
          }
          this.$store.commit("players/clear");
        },
      });
    },
    clearRoles() {
      this.$store.commit("setConfirmModal", {
        title: this.$t("settings.clearRoles"),
        message: this.$t("settings.confirmClearRoles"),
        confirmText: this.$t("common.confirm"),
        cancelText: this.$t("common.cancel"),
        onConfirm: () => {
          this.$store.dispatch("players/clearRoles");
        },
      });
    },
    toggleNight() {
      this.$store.commit("toggleNight");
      if (this.grimoire.isNight) {
        this.$store.commit("session/setMarkedPlayer", -1);
      }
    },
    toggleLanguage() {
      const newLocale = this.$i18n.locale === "en" ? "zh-CN" : "en";
      this.$i18n.locale = newLocale;
      this.$store.commit("setLocale", newLocale);
    },
    setNickname() {
      this.$store.commit("toggleModal", "nickname");
    },
    clearStorage() {
      this.$store.commit("setConfirmModal", {
        title: this.$t("settings.clearStorage"),
        message: this.$t("settings.confirmClearStorage"),
        confirmText: this.$t("common.confirm"),
        cancelText: this.$t("common.cancel"),
        onConfirm: () => {
          // Clear all localStorage
          localStorage.clear();
          // Reload the page to reset the application
          window.location.reload();
        },
      });
    },
    ...mapMutations([
      "toggleGrimoire",
      "toggleMenu",
      "toggleImageOptIn",
      "toggleMuted",
      "toggleNightOrder",
      "toggleStatic",
      "setZoom",
      "toggleModal",
    ]),
  },
};
</script>

<style scoped lang="scss">
@import "../vars.scss";

// success animation
@keyframes greenToWhite {
  from {
    color: green;
  }
  to {
    color: white;
  }
}

// Controls
#controls {
  position: absolute;
  right: 3px;
  top: 3px;
  text-align: right;
  padding-right: 50px;
  z-index: 75;

  svg {
    filter: drop-shadow(0 0 5px rgba(0, 0, 0, 1));
    &.success {
      animation: greenToWhite 1s normal forwards;
      animation-iteration-count: 1;
    }
  }

  > span {
    display: inline-block;
    cursor: pointer;
    z-index: 5;
    margin-top: 7px;
    margin-left: 10px;
  }

  span.nomlog-summary {
    color: $townsfolk;
  }

  span.session {
    color: $demon;
    &.spectator {
      color: $townsfolk;
    }
    &.reconnecting {
      animation: blink 1s infinite;
    }
  }
}

@keyframes blink {
  50% {
    opacity: 0.5;
    color: gray;
  }
}

.menu {
  width: 220px;
  transform-origin: 200px 22px;
  transition: transform 500ms cubic-bezier(0.68, -0.55, 0.27, 1.55);
  transform: rotate(-90deg);
  position: absolute;
  right: 0;
  top: 0;

  &.open {
    transform: rotate(0deg);
  }

  > svg {
    cursor: pointer;
    background: rgba(0, 0, 0, 0.5);
    border: 3px solid black;
    width: 40px;
    height: 50px;
    margin-bottom: -8px;
    border-bottom: 0;
    border-radius: 10px 10px 0 0;
    padding: 5px 5px 15px;
  }

  a {
    color: white;
    text-decoration: none;
    &:hover {
      color: red;
    }
  }

  ul {
    display: flex;
    list-style-type: none;
    padding: 0;
    margin: 0;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 0 10px black;
    border: 3px solid black;
    border-radius: 10px 0 10px 10px;

    li {
      padding: 2px 5px;
      color: white;
      text-align: left;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 30px;

      &.tabs {
        display: flex;
        padding: 0;
        svg {
          flex-grow: 1;
          flex-shrink: 0;
          height: 35px;
          border-bottom: 3px solid black;
          border-right: 3px solid black;
          padding: 5px 0;
          cursor: pointer;
          transition: color 250ms;
          &:hover {
            color: red;
          }
          &:last-child {
            border-right: 0;
          }
        }
        &.grimoire .fa-book-open,
        &.players .fa-users,
        &.characters .fa-theater-masks,
        &.session .fa-broadcast-tower,
        &.help .fa-question {
          background: linear-gradient(
            to bottom,
            $townsfolk 0%,
            rgba(0, 0, 0, 0.5) 100%
          );
        }
      }

      &:not(.headline):not(.tabs):hover {
        cursor: pointer;
        color: red;
      }

      em {
        flex-grow: 0;
        font-style: normal;
        margin-left: 10px;
        font-size: 80%;
      }
    }

    .headline {
      font-family: PiratesBay, sans-serif;
      letter-spacing: 1px;
      padding: 0 10px;
      text-align: center;
      justify-content: center;
      background: linear-gradient(
        to right,
        $townsfolk 0%,
        rgba(0, 0, 0, 0.5) 20%,
        rgba(0, 0, 0, 0.5) 80%,
        $demon 100%
      );
    }
  }
}
</style>
